import { CopySVG } from 'common/assets';
import { themedStyled } from 'common/styles/styletron.theme';
import DeleteButton from 'components/common/delete-icon-button';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { TextLabelPlacementEnum } from 'components/elements/custom-form-control';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import HotelSelectInput from 'legacy-modules/master/hotel/components/select-input';
import {
  CardSection,
  SeparatorHorizontal,
  SectionContainer as RawSectionContainer,
  HalfContainer,
  FullContainer,
  RowBetweenStyles,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import ScheduleAddOnsTable from './add-ons-table';
import ScheduleGuestTable from './guest-table';
import ScheduleHotelTable from './hotel-table';
import { ScheduleCardHeader, ScheduleCardHeaderLeft } from '../../style';
import { HotelPurchaseFormType } from '../form-type';

interface Props {
  parentIndex: number;
  onRemove: () => void;
  fieldLength?: number;
}

// function transformer(item: any): Option {
//   return {
//     id: item.id,
//     label: `${item.type} (${item.size})`,
//   };
// }

export default function ScheduleCard(props: Props) {
  const { t } = useTranslation();
  const { getValues, setValue, control, trigger } =
    useFormContext<HotelPurchaseFormType>();

  const { editable } = useFormState();

  const name = 'hotelPurchases' as const;

  const onDuplicate = React.useCallback(() => {
    const hotelPurchases = getValues(name);
    const hotelPurchase = hotelPurchases[props.parentIndex];
    setValue(name, [...hotelPurchases, hotelPurchase]);
  }, [getValues, props.parentIndex, setValue]);

  const { isCityTax } = useWatch({
    control,
    name: `${name}.${props.parentIndex}`,
  });

  const hotelId = getValues(`hotelPurchases.${props.parentIndex}.hotelId`);
  React.useEffect(() => {
    if (!hotelId || hotelId === '') {
      setValue(`hotelPurchases.${props.parentIndex}.hotelName`, '');
      setValue(`hotelPurchases.${props.parentIndex}.hotelPhone`, '');
      setValue(`hotelPurchases.${props.parentIndex}.hotelEmail`, '');
      setValue(`hotelPurchases.${props.parentIndex}.hotelAddress`, '');
      setValue(`hotelPurchases.${props.parentIndex}.defaultCheckInOut`, {
        in: '',
        out: '',
      });
    }
  }, [hotelId, props.parentIndex, setValue]);

  return (
    <CardSection>
      <ScheduleCardHeader>
        <ScheduleCardHeaderLeft>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 16,
              alignItems: 'center',
            }}
          >
            <Text variant="HeadingXSmall">
              {t('ticketOrders:schedule_extra', {
                extra: props.parentIndex + 1,
              })}
            </Text>
            {editable && (
              <Button
                variant="default"
                startEnhancer={(color, size) => (
                  <CopySVG color={color} size={size} />
                )}
                buttonType="tertiary"
                onClick={onDuplicate}
              >
                {t('common:duplicate_schedule')}
              </Button>
            )}
          </div>
          <Input
            type="text"
            name={`hotelPurchases.${props.parentIndex}.bookingCode`}
            label={t('common:booking_code')}
            labelPlacement={TextLabelPlacementEnum.Left}
            placeholder={t('common:booking_code')}
            noMargin
          />
        </ScheduleCardHeaderLeft>
        {(props.fieldLength || 0) > 1 && editable && (
          <>
            <SeparatorHorizontal $margin={16} />
            <DeleteButton
              onClick={props.onRemove}
              rootStyle={{ height: '32px' }}
            />
          </>
        )}
      </ScheduleCardHeader>
      <Separator gap={24} />
      <SectionContainer>
        <HalfContainer>
          <HotelSelectInput
            name={`hotelPurchases.${props.parentIndex}.hotelId`}
            label={t('common:hotel')}
            onAfterDetailChange={(hotel) => {
              // setHotelRoomList(
              //   hotel.hotelRooms.map((hotelRoom) => transformer(hotelRoom)),
              // );
              setValue(
                `hotelPurchases.${props.parentIndex}.hotelName`,
                hotel.name || '',
              );
              setValue(
                `hotelPurchases.${props.parentIndex}.hotelPhone`,
                hotel.phone || '',
              );
              setValue(
                `hotelPurchases.${props.parentIndex}.hotelEmail`,
                hotel.email || '',
              );
              setValue(
                `hotelPurchases.${props.parentIndex}.hotelAddress`,
                hotel.address || '',
              );
              setValue(
                `hotelPurchases.${props.parentIndex}.defaultCheckInOut`,
                {
                  in: hotel.checkIn || '',
                  out: hotel.checkOut || '',
                },
              );
              trigger(name);
            }}
            required
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <FullContainer $style={RowBetweenStyles}>
            <FullContainer>
              <Input
                type="text"
                name={`hotelPurchases.${props.parentIndex}.hotelPhone`}
                label={t('common:phone_number')}
                disabled
              />
            </FullContainer>
            <SeparatorHorizontal $margin={16} />
            <FullContainer>
              <Input
                type="email"
                name={`hotelPurchases.${props.parentIndex}.hotelEmail`}
                label={t('common:email')}
                disabled
              />
            </FullContainer>
          </FullContainer>
        </HalfContainer>
        <HalfContainer>
          <Input
            type="text"
            name={`hotelPurchases.${props.parentIndex}.hotelAddress`}
            label={t('common:address')}
            disabled
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <FullContainer $style={RowBetweenStyles}>
            <FullContainer>
              <Input
                type="text"
                name={`hotelPurchases.${props.parentIndex}.defaultCheckInOut.in`}
                label={t('common:check_in')}
                disabled
              />
            </FullContainer>
            <SeparatorHorizontal $margin={16} />
            <FullContainer>
              <Input
                type="text"
                name={`hotelPurchases.${props.parentIndex}.defaultCheckInOut.out`}
                label={t('common:check_out')}
                disabled
              />
            </FullContainer>
          </FullContainer>
        </HalfContainer>
        <HalfContainer>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Input
              type="switch"
              name={`hotelPurchases.${props.parentIndex}.isByPax`}
              noMargin
            />
            <Text variant="LabelMedium">{t('common:pricing_by_per_pax')}</Text>
          </div>
        </HalfContainer>
      </SectionContainer>
      <ScheduleGuestTable parentIndex={props.parentIndex} />
      <Separator gap={24} />
      <ScheduleHotelTable parentIndex={props.parentIndex} />
      <Separator gap={24} />
      <ScheduleAddOnsTable parentIndex={props.parentIndex} />
      <Separator gap={12} />
      <SectionContainer>
        <HalfContainer>
          <Input
            type="check-box"
            name={`hotelPurchases.${props.parentIndex}.isCityTax`}
            label={t('common:city_tax')}
            onAfterChange={() =>
              setValue(`hotelPurchases.${props.parentIndex}.cityTax`, '', {
                shouldTouch: true,
                shouldValidate: true,
              })
            }
            noMargin
          />
          <Input
            type="text"
            name={`hotelPurchases.${props.parentIndex}.cityTax`}
            placeholder={t('common:city_tax')}
            disabled={!isCityTax}
            required={isCityTax}
            hideRequiredStar
          />
        </HalfContainer>
      </SectionContainer>
    </CardSection>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
